import {
  Box, Container, Typography, Grid2 as Grid, Card, CardContent,
  CardActions, Button, Chip, Tabs, Tab, Skeleton, Divider, Alert
} from "@mui/material"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { fetchFlaggedWallets } from "../../../redux/admin-slices/wallet.slice"
import PauseResumeDialog from "../../../components/Dialog/PauseResumeDialog"
import FlagUnflagDialog from "../../../components/Dialog/FlagUnflagDialog"
import { formatDate } from "../../../utils/helper"

const fmt = (n, cur = "inr") => {
  const isUsd = cur?.toLowerCase() === "usd";
  const symbol = isUsd ? "$" : "₹";
  const locale = isUsd ? "en-US" : "en-IN";
  return `${symbol}${(n / 100).toLocaleString(locale, { minimumFractionDigits: 2 })}`;
}

/* ──────────────────────────────────────────────────────────────────────────── */

function RiskCard({ specialist, onResume, onUnflag }) {
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", height: "100%" }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
          <Box>
            <Typography fontWeight={700} textTransform="capitalize">{specialist.fullName}</Typography>
            <Typography fontSize={12} color="text.secondary">{specialist.email}</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-end" }}>
            {specialist.flaggedForReview && (
              <Chip size="small" label="🚩 Flagged" color="error" sx={{ fontWeight: 600 }} />
            )}
            {specialist.payoutPaused && (
              <Chip size="small" label="⏸ Paused" color="warning" sx={{ fontWeight: 600 }} />
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Balance */}
        <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
          <Box>
            <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase">Pending</Typography>
            <Typography fontSize={14} fontWeight={600}>{fmt(specialist.pendingBalance, specialist.currency)}</Typography>
          </Box>
          <Box>
            <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase">Available</Typography>
            <Typography fontSize={14} fontWeight={700} color="success.main">
              {fmt(specialist.availableBalance, specialist.currency)}
            </Typography>
          </Box>
        </Box>

        {/* Flag reason */}
        {specialist.flaggedForReview && specialist.flagReason && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }} icon={false}>
            <strong>Flag reason:</strong> {specialist.flagReason}
            {specialist.flaggedAt && (
              <Typography fontSize={11} color="inherit" mt={0.5}>
                {formatDate(specialist.flaggedAt)}
              </Typography>
            )}
          </Alert>
        )}

        {/* Pause reason */}
        {specialist.payoutPaused && specialist.payoutPausedReason && (
          <Alert severity="warning" sx={{ mb: 1, fontSize: 12 }} icon={false}>
            <strong>Pause reason:</strong> {specialist.payoutPausedReason}
          </Alert>
        )}
      </CardContent>

      <Divider />
      <CardActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: "wrap" }}>
        {specialist.payoutPaused && (
          <Button size="small" variant="outlined" color="success"
            onClick={() => onResume(specialist)}
            sx={{ textTransform: "none", fontSize: 12 }}>
            ▶ Resume Payouts
          </Button>
        )}
        {specialist.flaggedForReview && (
          <Button size="small" variant="outlined" color="warning"
            onClick={() => onUnflag(specialist)}
            sx={{ textTransform: "none", fontSize: 12 }}>
            ✓ Remove Flag
          </Button>
        )}
      </CardActions>
    </Card>
  )
}

/* ──────────────────────────────────────────────────────────────────────────── */

export default function WalletRisk() {
  const dispatch  = useDispatch()
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab,     setTab]     = useState(0) // 0 = paused, 1 = flagged

  const [pauseTarget, setPauseTarget] = useState(null)
  const [flagTarget,  setFlagTarget]  = useState(null)

  const load = async () => {
    setLoading(true)
    try {
      const res = await dispatch(fetchFlaggedWallets({ limit: 100 })).unwrap()
      setData(res)
    } catch { /* toast */ }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, []) // eslint-disable-line

  const allWallets  = data?.wallets ?? []
  const paused  = allWallets.filter(w => w.payoutPaused)
  const flagged = allWallets.filter(w => w.flaggedForReview)
  const listed  = tab === 0 ? paused : flagged

  return (
    <Container maxWidth={false} fixed sx={{ py: 5 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="pageTitle">Risk Control Center</Typography>
        <Typography color="text.secondary" fontSize={14} mt={0.5}>
          Specialists with paused or flagged wallets. Resolve each case to re-enable payouts.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, "& .MuiTab-root": { fontWeight: 600 } }}
      >
        <Tab label={`⏸ Paused Payouts (${paused.length})`} />
        <Tab label={`🚩 Flagged for Review (${flagged.length})`} />
      </Tabs>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
                <CardContent>
                  <Skeleton height={24} width="60%" />
                  <Skeleton height={16} width="40%" />
                  <Skeleton height={60} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : listed.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography fontSize={40}>🎉</Typography>
          <Typography fontWeight={700} fontSize={18} mt={1}>
            {tab === 0 ? "No paused wallets" : "No flagged accounts"}
          </Typography>
          <Typography color="text.secondary" fontSize={14} mt={0.5}>
            {tab === 0 ? "All specialists can receive payouts." : "No accounts require review."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {listed.map((s, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, lg: 4 }}>
              <RiskCard
                specialist={s}
                onResume={(sp) => setPauseTarget({ specialist: sp, mode: "resume" })}
                onUnflag={(sp) => setFlagTarget({ specialist: sp, mode: "unflag" })}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <PauseResumeDialog
        open={Boolean(pauseTarget)}
        mode={pauseTarget?.mode}
        specialist={pauseTarget?.specialist}
        onClose={() => setPauseTarget(null)}
        onSuccess={() => { setPauseTarget(null); load() }}
      />
      <FlagUnflagDialog
        open={Boolean(flagTarget)}
        mode={flagTarget?.mode}
        specialist={flagTarget?.specialist}
        onClose={() => setFlagTarget(null)}
        onSuccess={() => { setFlagTarget(null); load() }}
      />
    </Container>
  )
}
